import { logEvent } from "../lib/analytics.safe";
import { createCheckoutSessionHttp } from "@/lib/cf";

type CheckoutPlan = "monthly" | "annual";

type StartCheckoutOptions = {
  returnTo?: string;
  plan?: CheckoutPlan;
  promoCode?: string | null;
  navigate?: (url: string) => void;
};

const DEFAULT_RETURN_TO = "/cases";

const assignCheckoutUrl = (href: string) => {
  if (typeof window === "undefined") return;
  const win = window as typeof window & { Cypress?: unknown; __cypressCheckoutUrl?: string };
  if (win.Cypress) {
    // Store URL for Cypress visibility AND navigate
    win.__cypressCheckoutUrl = href;
    window.location.assign(href);
    return;
  }
  window.location.assign(href);
};

export const safeAssignCheckoutUrl = assignCheckoutUrl;

export async function startCheckout(options: StartCheckoutOptions = {}) {
  const { plan = "monthly", promoCode, navigate } = options;
  const returnTo = options.returnTo ?? DEFAULT_RETURN_TO;

  const origin = window.location.origin;
  const buildBillingUrl = (activated: boolean) => {
    const search = new URLSearchParams();
    if (activated) search.set("activated", "1");
    if (returnTo) search.set("returnTo", returnTo);
    const query = search.toString();
    return query ? `${origin}/billing?${query}` : `${origin}/billing`;
  };

  // Send customers through the Billing funnel; activated flag ensures first return lands on profile
  const successUrl = buildBillingUrl(true);
  const cancelUrl = buildBillingUrl(false);

  const trimmedPromo = promoCode?.trim();

  // Track checkout started event for analytics & funnel monitoring
  logEvent("billing_checkout_started", {
    return_url: successUrl,
    event_category: "billing",
    plan,
    promo_code: trimmedPromo ?? null,
  });

  const payload = {
    plan,
    promoCode: trimmedPromo || undefined,
    successUrl,
    cancelUrl,
    returnUrl: successUrl,
  } as const;

  const { url } = await createCheckoutSessionHttp(payload);
  if (!url) throw new Error("Checkout URL missing");
  const go = navigate ?? assignCheckoutUrl;
  go(url);
}